import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import MarkdownRenderer from "@/components/MarkdownRenderer";
import { Eye, Edit3, Bold, Italic, List, Link as LinkIcon, Heading2, Quote } from "lucide-react";

/**
 * Split-pane Markdown editor with toolbar + live preview toggle.
 */
export default function MarkdownEditor({ value, onChange, rows = 14, testid = "markdown-editor" }) {
  const [mode, setMode] = useState("edit");

  const insertAtCursor = (before, after = "") => {
    const el = document.getElementById(`${testid}-textarea`);
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const sel = (value || "").substring(start, end);
    const next = (value || "").substring(0, start) + before + sel + after + (value || "").substring(end);
    onChange(next);
    setTimeout(() => {
      el.focus();
      el.selectionStart = start + before.length;
      el.selectionEnd = end + before.length;
    }, 0);
  };

  const tools = [
    { icon: Heading2, label: "H2", onClick: () => insertAtCursor("\n## ", "\n") },
    { icon: Bold, label: "Bold", onClick: () => insertAtCursor("**", "**") },
    { icon: Italic, label: "Italic", onClick: () => insertAtCursor("_", "_") },
    { icon: LinkIcon, label: "Link", onClick: () => insertAtCursor("[", "](https://)") },
    { icon: List, label: "List", onClick: () => insertAtCursor("\n- ", "") },
    { icon: Quote, label: "Quote", onClick: () => insertAtCursor("\n> ", "\n") },
  ];

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card" data-testid={testid}>
      <div className="flex items-center justify-between border-b border-border px-3 py-2 bg-secondary/40">
        <div className="flex items-center gap-1">
          {tools.map((t) => (
            <button
              key={t.label}
              type="button"
              onClick={t.onClick}
              title={t.label}
              className="p-2 rounded-md hover:bg-secondary transition-colors"
            >
              <t.icon className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider">
          <button
            type="button"
            onClick={() => setMode("edit")}
            data-testid={`${testid}-mode-edit`}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md ${mode === "edit" ? "bg-foreground text-background" : "hover:bg-secondary"}`}
          >
            <Edit3 className="w-3 h-3" /> Edit
          </button>
          <button
            type="button"
            onClick={() => setMode("preview")}
            data-testid={`${testid}-mode-preview`}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md ${mode === "preview" ? "bg-foreground text-background" : "hover:bg-secondary"}`}
          >
            <Eye className="w-3 h-3" /> Preview
          </button>
        </div>
      </div>
      {mode === "edit" ? (
        <textarea
          id={`${testid}-textarea`}
          rows={rows}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testid}-input`}
          spellCheck={true}
          className="w-full bg-transparent px-4 py-3 font-mono text-sm outline-none resize-y min-h-[300px] leading-relaxed"
          placeholder={"Write in Markdown. Use ## for headings, **bold**, _italic_, [link](url), lists with - …"}
        />
      ) : (
        <div className="p-6 min-h-[300px] max-h-[600px] overflow-y-auto" data-testid={`${testid}-preview`}>
          {value ? <MarkdownRenderer>{value}</MarkdownRenderer> : <div className="text-muted-foreground font-mono text-xs">[ empty ]</div>}
        </div>
      )}
    </div>
  );
}
